import type { Signal } from "./schemas";

/** A pick joined to its live signal. Keyed by id because labels are not
 *  guaranteed unique — an organiser can create two signals with one label. */
export interface ResolvedSignal {
  id: string;
  label: string;
}

/**
 * Join picked ids to the live signal list, in pick order, dropping ids whose
 * signal has been deleted. Every surface that shows or edits a selection must
 * resolve through here so a dangling id stays a display no-op everywhere.
 */
export function resolveSignals(
  signals: Signal[],
  signalIds: string[],
): ResolvedSignal[] {
  const labelById = new Map(signals.map((signal) => [signal.id, signal.label]));
  return signalIds.flatMap((id) => {
    const label = labelById.get(id);
    return label ? [{ id, label }] : [];
  });
}

/**
 * Sorted client-side rather than by an orderBy in the query: Firestore drops
 * documents missing the ordered field from the result set entirely, and a
 * console-seeded signal without displayOrder would silently vanish from both
 * the picker and the admin tab.
 */
export function sortSignals(signals: Signal[]): Signal[] {
  return [...signals].sort(
    (a, b) =>
      (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
      a.label.localeCompare(b.label),
  );
}
